import { Folder, List, Section, ListItem, api } from '@/entities/all';
import type { Folder as FolderType, List as ListType, Section as SectionType, ListItem as ListItemType } from '@/types';

export const BUILTIN_FIELDS = [
  'id',
  'created_date',
  'updated_date',
  'created_by_id',
  'created_by',
  'is_sample',
] as const;

export const VALID_FOLDER_COLORS = ['blue', 'green', 'purple', 'orange', 'red', 'teal'] as const;

export interface ProfileData {
  version: number;
  exportedAt?: string;
  folders: Array<Record<string, any>>;
  lists: Array<Record<string, any>>;
  sections: Array<Record<string, any>>;
  listItems: Array<Record<string, any>>;
  inheritedCompletions?: Array<{ list_id: string; item_id: string; completed: boolean }>;
}

export interface ImportProgress {
  stage: 'folders' | 'lists' | 'inheritance' | 'sections' | 'items' | 'completions';
  current: number;
  total: number;
  message: string;
}

export interface ImportResult {
  foldersCount: number;
  listsCount: number;
  sectionsCount: number;
  itemsCount: number;
}

export function stripBuiltins<T extends Record<string, any>>(entity: T): Record<string, any> {
  const cleaned: Record<string, any> = { ...entity };
  for (const field of BUILTIN_FIELDS) {
    delete cleaned[field];
  }
  return cleaned;
}

export async function mapConcurrent<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let currentIndex = 0;

  async function worker() {
    while (currentIndex < items.length) {
      const index = currentIndex++;
      results[index] = await fn(items[index], index);
    }
  }

  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    () => worker()
  );
  await Promise.all(workers);
  return results;
}

/**
 * Recursively resolves all ancestor list IDs for a given list ID in a DAG.
 */
export function getAllAncestorListIds(
  listId: string,
  lists: Array<{ id: string; inherited_list_ids?: string[] }>
): string[] {
  const ancestorIds: string[] = [];
  const visited = new Set<string>();
  const initialInherited = lists.find((l) => l.id === listId)?.inherited_list_ids || [];
  const queue = [...initialInherited];

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    if (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      ancestorIds.push(currentId);
      const currentList = lists.find((l) => l.id === currentId);
      if (currentList?.inherited_list_ids && Array.isArray(currentList.inherited_list_ids)) {
        for (const parentId of currentList.inherited_list_ids) {
          if (parentId && !visited.has(parentId)) {
            queue.push(parentId);
          }
        }
      }
    }
  }

  return ancestorIds;
}

export async function exportProfile(): Promise<ProfileData> {
  const allData = await api.data.getAll();

  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    folders: allData.folders || [],
    lists: allData.lists || [],
    sections: allData.sections || [],
    listItems: allData.items || [],
    inheritedCompletions: allData.inherited_completions || [],
  };
}

export function downloadProfile(profile: ProfileData, filename?: string): void {
  const blob = new Blob([JSON.stringify(profile, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `listify-profile-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function parseProfileFile(file: File): Promise<ProfileData> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        if (!text) {
          throw new Error('File is empty');
        }
        const parsed = JSON.parse(text);
        if (!parsed || typeof parsed !== 'object') {
          throw new Error('Invalid JSON format');
        }
        if (!Array.isArray(parsed.folders) && !Array.isArray(parsed.lists)) {
          throw new Error('Invalid profile file: missing folders or lists array');
        }
        resolve({
          version: parsed.version || 1,
          exportedAt: parsed.exportedAt,
          folders: Array.isArray(parsed.folders) ? parsed.folders : [],
          lists: Array.isArray(parsed.lists) ? parsed.lists : [],
          sections: Array.isArray(parsed.sections) ? parsed.sections : [],
          listItems: Array.isArray(parsed.listItems)
            ? parsed.listItems
            : Array.isArray(parsed.items)
            ? parsed.items
            : [],
          inheritedCompletions: Array.isArray(parsed.inheritedCompletions)
            ? parsed.inheritedCompletions
            : Array.isArray(parsed.inherited_completions)
            ? parsed.inherited_completions
            : [],
        });
      } catch (err: any) {
        reject(new Error(err.message || 'Failed to parse profile JSON'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

export async function importProfile(
  profile: ProfileData,
  onProgress?: (progress: ImportProgress) => void,
  concurrency = 5
): Promise<ImportResult> {
  if (!profile || typeof profile !== 'object') {
    throw new Error('Invalid profile data');
  }

  const rawFolders: Array<Record<string, any>> = Array.isArray(profile.folders) ? profile.folders : [];
  const rawLists: Array<Record<string, any>> = Array.isArray(profile.lists) ? profile.lists : [];
  const rawSections: Array<Record<string, any>> = Array.isArray(profile.sections) ? profile.sections : [];
  const rawItems: Array<Record<string, any>> = Array.isArray(profile.listItems)
    ? profile.listItems
    : Array.isArray((profile as any).items)
    ? (profile as any).items
    : [];
  const rawCompletions: Array<Record<string, any>> = Array.isArray(profile.inheritedCompletions)
    ? profile.inheritedCompletions
    : Array.isArray((profile as any).inherited_completions)
    ? (profile as any).inherited_completions
    : [];

  const folderIdMap: Record<string, string> = {};
  const listIdMap: Record<string, string> = {};
  const sectionIdMap: Record<string, string> = {};
  const itemIdMap: Record<string, string> = {};

  // 1. Import Folders
  let completedFolders = 0;
  onProgress?.({
    stage: 'folders',
    current: 0,
    total: rawFolders.length,
    message: `Importing folders (0/${rawFolders.length})...`,
  });

  const newFolders = await mapConcurrent(rawFolders, concurrency, async (folder) => {
    const data = stripBuiltins(folder);
    const validColor = VALID_FOLDER_COLORS.includes(data.color) ? data.color : 'blue';
    const created = await Folder.create({
      name: (data.name || 'Untitled Folder').trim() || 'Untitled Folder',
      color: validColor,
      position: typeof data.position === 'number' ? data.position : 0,
    });
    completedFolders++;
    onProgress?.({
      stage: 'folders',
      current: completedFolders,
      total: rawFolders.length,
      message: `Importing folders (${completedFolders}/${rawFolders.length})...`,
    });
    return { oldId: folder.id, created };
  });

  newFolders.forEach(({ oldId, created }) => {
    if (oldId && created?.id) {
      folderIdMap[oldId] = created.id;
    }
  });

  // 2. Import Lists (pass 1: create with folder_id, without inherited_list_ids)
  let completedLists = 0;
  onProgress?.({
    stage: 'lists',
    current: 0,
    total: rawLists.length,
    message: `Importing lists (0/${rawLists.length})...`,
  });

  const newLists = await mapConcurrent(rawLists, concurrency, async (list) => {
    const data = stripBuiltins(list);
    const newFolderId = list.folder_id ? folderIdMap[list.folder_id] || null : null;
    const created = await List.create({
      title: (data.title || 'Untitled List').trim() || 'Untitled List',
      folder_id: newFolderId,
      inherited_list_ids: [],
      position: typeof data.position === 'number' ? data.position : 0,
    });
    completedLists++;
    onProgress?.({
      stage: 'lists',
      current: completedLists,
      total: rawLists.length,
      message: `Importing lists (${completedLists}/${rawLists.length})...`,
    });
    return { oldId: list.id, created };
  });

  newLists.forEach(({ oldId, created }) => {
    if (oldId && created?.id) {
      listIdMap[oldId] = created.id;
    }
  });

  // 3. Update list inheritance (pass 2: map inherited_list_ids to new UUIDs)
  const listsWithInheritance = rawLists.filter(
    (list) => Array.isArray(list.inherited_list_ids) && list.inherited_list_ids.length > 0
  );

  let completedInheritance = 0;
  onProgress?.({
    stage: 'inheritance',
    current: 0,
    total: listsWithInheritance.length,
    message: `Linking inherited lists (0/${listsWithInheritance.length})...`,
  });

  await mapConcurrent(listsWithInheritance, concurrency, async (list) => {
    const newListId = listIdMap[list.id];
    if (!newListId) return;

    const inheritedIds = (list.inherited_list_ids || [])
      .map((oldId: string) => listIdMap[oldId])
      .filter(Boolean);

    if (inheritedIds.length > 0) {
      await List.update(newListId, { inherited_list_ids: inheritedIds });
    }
    completedInheritance++;
    onProgress?.({
      stage: 'inheritance',
      current: completedInheritance,
      total: listsWithInheritance.length,
      message: `Linking inherited lists (${completedInheritance}/${listsWithInheritance.length})...`,
    });
  });

  // 4. Import Sections
  let completedSections = 0;
  onProgress?.({
    stage: 'sections',
    current: 0,
    total: rawSections.length,
    message: `Importing sections (0/${rawSections.length})...`,
  });

  const newSections = await mapConcurrent(rawSections, concurrency, async (section) => {
    const newListId = listIdMap[section.list_id];
    if (!newListId) {
      return null;
    }
    const data = stripBuiltins(section);
    const created = await Section.create({
      list_id: newListId,
      title: data.title != null ? String(data.title) : '',
      position: typeof data.position === 'number' ? data.position : 0,
    });
    completedSections++;
    onProgress?.({
      stage: 'sections',
      current: completedSections,
      total: rawSections.length,
      message: `Importing sections (${completedSections}/${rawSections.length})...`,
    });
    return { oldId: section.id, created };
  });

  newSections.forEach((item) => {
    if (item && item.oldId && item.created?.id) {
      sectionIdMap[item.oldId] = item.created.id;
    }
  });

  // 5. Import List Items
  let completedItems = 0;
  onProgress?.({
    stage: 'items',
    current: 0,
    total: rawItems.length,
    message: `Importing items (0/${rawItems.length})...`,
  });

  const validItems = rawItems.filter((item: Record<string, any>) => sectionIdMap[item.section_id]);

  await mapConcurrent(validItems, concurrency, async (item: Record<string, any>) => {
    const newSectionId = sectionIdMap[item.section_id];
    if (!newSectionId) return null;

    const data = stripBuiltins(item);
    const rawContent = data.content != null ? String(data.content).trim() : '';
    const content = rawContent || 'Untitled Item';
    const created = await ListItem.create({
      section_id: newSectionId,
      content,
      completed: Boolean(data.completed),
      position: typeof data.position === 'number' ? data.position : 0,
    });
    if (item.id && created?.id) {
      itemIdMap[item.id] = created.id;
    }
    completedItems++;
    onProgress?.({
      stage: 'items',
      current: completedItems,
      total: validItems.length,
      message: `Importing items (${completedItems}/${validItems.length})...`,
    });
    return created;
  });

  // 6. Import Inherited Completions (if present)
  if (rawCompletions.length > 0) {
    const validCompletions = rawCompletions.filter(
      (c) => listIdMap[c.list_id] && itemIdMap[c.item_id]
    );

    let completedComps = 0;
    onProgress?.({
      stage: 'completions',
      current: 0,
      total: validCompletions.length,
      message: `Restoring inherited item states (0/${validCompletions.length})...`,
    });

    await mapConcurrent(validCompletions, concurrency, async (c) => {
      const newListId = listIdMap[c.list_id];
      const newItemId = itemIdMap[c.item_id];
      if (!newListId || !newItemId) return;

      try {
        await List.setInheritedItemCompleted(newListId, newItemId, Boolean(c.completed));
      } catch (err) {
        console.warn('Failed to set inherited completion during import:', err);
      }
      completedComps++;
      onProgress?.({
        stage: 'completions',
        current: completedComps,
        total: validCompletions.length,
        message: `Restoring inherited item states (${completedComps}/${validCompletions.length})...`,
      });
    });
  }

  return {
    foldersCount: newFolders.length,
    listsCount: newLists.length,
    sectionsCount: newSections.filter(Boolean).length,
    itemsCount: completedItems,
  };
}
